/**
 * Symphony GROUP CHAT, Phase 10c — WhatsApp's message types, the pure half: reading a typed row
 * (`metadata.groupContent`, `groupPollVotes`, `groupEventRsvps`), checking what a person is about to send, and every
 * decision the bubbles and the composer make (which kind a picked file is, what a tap on a poll option or an RSVP
 * does, when a file link is stale, the voice waveform). No React, no fetch: iOS (`GroupContent.swift`) and Android
 * (`GroupContent.kt`) carry the same rules, case for case (`group-content.test.ts` · `GroupChatPhase10cTests` ·
 * `GroupChatPhase10cTest`).
 */

import stickerManifest from '../public/stickers/manifest.json';

export type GroupContentType = 'IMAGE' | 'VIDEO' | 'FILE' | 'VOICE_NOTE' | 'POLL' | 'EVENT' | 'LOCATION' | 'CONTACT' | 'STICKER' | 'GIF';
export type GroupMediaType = 'IMAGE' | 'VIDEO' | 'FILE' | 'VOICE_NOTE' | 'GIF';
export type GroupRsvp = 'GOING' | 'MAYBE' | 'NOT_GOING';

export const GROUP_MEDIA_TYPES: readonly GroupMediaType[] = ['IMAGE', 'VIDEO', 'FILE', 'VOICE_NOTE', 'GIF'];
export const GROUP_CAPTION_TYPES: readonly GroupContentType[] = ['IMAGE', 'VIDEO', 'FILE', 'GIF'];
const ALL_TYPES: readonly GroupContentType[] = ['IMAGE', 'VIDEO', 'FILE', 'VOICE_NOTE', 'POLL', 'EVENT', 'LOCATION', 'CONTACT', 'STICKER', 'GIF'];

export interface GroupMedia {
  mediaId: string;
  mimeType: string;
  sizeBytes: number;
  fileName: string;
  width: number;
  height: number;
  durationMs: number;
  waveform: number[];
}
export interface GroupPollOption {
  id: string;
  text: string;
}
export interface GroupPoll {
  question: string;
  options: GroupPollOption[];
  multipleAnswers: boolean;
}
export interface GroupCalendarEvent {
  title: string;
  startDate: string;
  endDate: string | null;
  location: string;
  description: string;
}
export interface GroupLocation {
  latitude: number;
  longitude: number;
  label: string;
  address: string;
}
export interface GroupContactCard {
  name: string;
  phones: string[];
  emails: string[];
}
export interface GroupContent {
  type: GroupContentType;
  caption: string;
  media?: GroupMedia;
  poll?: GroupPoll;
  event?: GroupCalendarEvent;
  location?: GroupLocation;
  contact?: GroupContactCard;
  stickerId?: string;
}
export interface GroupPollVote {
  metaSiteId: string;
  optionIds: string[];
  date: string | null;
}
export interface GroupEventRsvp {
  metaSiteId: string;
  response: GroupRsvp;
  date: string | null;
}

/**
 * The built-in sticker set, in order: `public/stickers/manifest.json`, the ONE list web, iOS and Android read
 * (`test/sticker-manifest-parity.test.ts` pins the phones' copies to it; VMR GroupContentRules.STICKERS must match).
 * Each sticker is an animated Noto emoji (Google, CC BY 4.0 — see `public/stickers/NOTICE.md`) served from
 * `/stickers/<version>/<id>.webp`; the emoji is what it stands for in words, and what draws while the art loads.
 */
export const GROUP_STICKERS: readonly { id: string; emoji: string }[] = stickerManifest.stickers.map(({ id, emoji }) => ({ id, emoji }));
/** The art version: a new folder for new art, never new bytes under an old URL (the files are cached as immutable). */
export const STICKER_ART_VERSION: string = stickerManifest.version;
/** The sticker's animated WebP, mount-relative (prefix the base path); null for an id outside the set. */
export const stickerPath = (id: string | undefined): string | null =>
  id && GROUP_STICKERS.some((s) => s.id === id) ? `/stickers/${STICKER_ART_VERSION}/${id}.webp` : null;
export const stickerEmoji = (id: string | undefined): string | null => GROUP_STICKERS.find((s) => s.id === id)?.emoji ?? null;

const MiB = 1024 * 1024;
export const GROUP_MEDIA_MAX_BYTES: Record<GroupMediaType, number> = { IMAGE: 16 * MiB, VOICE_NOTE: 16 * MiB, GIF: 16 * MiB, VIDEO: 64 * MiB, FILE: 100 * MiB };
export const GROUP_MEDIA_LIMIT_LABEL: Record<GroupMediaType, string> = { IMAGE: '16 MB', VOICE_NOTE: '16 MB', GIF: '16 MB', VIDEO: '64 MB', FILE: '100 MB' };
export const GROUP_CONTENT_RULES = {
  maxCaption: 4000,
  maxQuestion: 300,
  minOptions: 2,
  maxOptions: 12,
  maxOptionText: 100,
  maxEventTitle: 200,
  maxEventLocation: 300,
  maxEventDescription: 2000,
  maxLocationLabel: 200,
  maxLocationAddress: 300,
  maxContactName: 200,
  maxContactValues: 5,
  waveformBars: 64,
  voiceMinMs: 1000,
  voiceMaxMs: 15 * 60 * 1000,
  /** A file link is fresh while it has more than this left. */
  linkFreshMs: 60_000,
  /** An id the links answer left out is not asked for again for this long. */
  linkRetryMs: 30_000,
  maxLinkBatch: 50,
  /** The service's bounds on what the sender measured: a value past them is dropped, never sent (it would 400). */
  maxPixels: 20000,
  maxDurationMs: 2 * 60 * 60 * 1000,
} as const;

/** What the sender measured, kept only within the service's bounds (a panorama past 20,000 px goes without a size). */
export function measuredWithinBounds(m: { width?: number; height?: number; durationMs?: number }): { width: number; height: number; durationMs: number } {
  const px = (v: number | undefined) => (v && Number.isFinite(v) && v > 0 && v <= GROUP_CONTENT_RULES.maxPixels ? Math.round(v) : 0);
  const width = px(m.width);
  const height = px(m.height);
  const d = m.durationMs && Number.isFinite(m.durationMs) && m.durationMs > 0 && m.durationMs <= GROUP_CONTENT_RULES.maxDurationMs ? Math.round(m.durationMs) : 0;
  return width && height ? { width, height, durationMs: d } : { width: 0, height: 0, durationMs: d };
}

/** A fetched link's usable expiry: the server's, but never under two minutes from the fetch (a fast client clock must not loop). */
export function linkExpiry(expiresDate: string | null | undefined, fetchedAt: number): number {
  const parsed = expiresDate ? Date.parse(expiresDate) : NaN;
  const floor = fetchedAt + 2 * GROUP_CONTENT_RULES.linkFreshMs + 1000;
  return Number.isFinite(parsed) ? Math.max(parsed, floor) : fetchedAt + 5 * 60_000;
}

const IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const VIDEO_MIMES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/3gpp'];
const GIF_MIMES = ['image/gif', 'video/mp4'];

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number => {
  const n = typeof v === 'string' && v.trim() ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
};
const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : []);

/** `type/subtype`, lower-cased, parameters dropped ("audio/webm;codecs=opus" → "audio/webm"). */
export function normalizedMime(mime: string | null | undefined): string {
  const m = (mime ?? '').trim().toLowerCase();
  const semi = m.indexOf(';');
  return (semi >= 0 ? m.slice(0, semi) : m).trim();
}

export const isMediaType = (t: GroupContentType): t is GroupMediaType => (GROUP_MEDIA_TYPES as readonly string[]).includes(t);
export const takesCaption = (t: GroupContentType) => GROUP_CAPTION_TYPES.includes(t);

// ─── Reading a row ───────────────────────────────────────────────────────────────────────────────────────────

function parseMedia(raw: unknown): GroupMedia | null {
  if (!isObject(raw) || !str(raw.mediaId)) return null;
  const waveform = Array.isArray(raw.waveform) ? raw.waveform.map(num).map((b) => Math.max(0, Math.min(100, Math.round(b)))) : [];
  return {
    mediaId: str(raw.mediaId),
    mimeType: normalizedMime(str(raw.mimeType)),
    // int64 → the proto JSON prints it as a string.
    sizeBytes: Math.max(0, num(raw.sizeBytes)),
    fileName: str(raw.fileName),
    width: Math.max(0, Math.round(num(raw.width))),
    height: Math.max(0, Math.round(num(raw.height))),
    durationMs: Math.max(0, Math.round(num(raw.durationMs))),
    waveform,
  };
}

/**
 * `metadata.groupContent`, or null — and null too when the part its type needs is missing or a sticker is unknown, so
 * the row draws as an ordinary text post of its own `content` (the server's summary). `metadata.groupDeleted` is
 * checked by the caller first: a tombstone wins.
 */
export function parseGroupContent(metadata: unknown): GroupContent | null {
  if (!isObject(metadata) || !isObject(metadata.groupContent)) return null;
  const raw = metadata.groupContent;
  const type = raw.type as GroupContentType;
  if (!ALL_TYPES.includes(type)) return null;
  const caption = str(raw.caption);
  if (isMediaType(type)) {
    const media = parseMedia(raw.media);
    return media ? { type, caption, media } : null;
  }
  switch (type) {
    case 'POLL': {
      const p = raw.poll;
      if (!isObject(p) || !str(p.question) || !Array.isArray(p.options)) return null;
      const options = p.options.filter(isObject).map((o) => ({ id: str(o.id), text: str(o.text) })).filter((o) => o.id && o.text);
      return options.length >= 2 ? { type, caption: '', poll: { question: str(p.question), options, multipleAnswers: p.multipleAnswers === true } } : null;
    }
    case 'EVENT': {
      const e = raw.event;
      if (!isObject(e) || !str(e.title) || !str(e.startDate) || !Number.isFinite(Date.parse(str(e.startDate)))) return null;
      const end = str(e.endDate);
      return {
        type,
        caption: '',
        event: { title: str(e.title), startDate: str(e.startDate), endDate: end && Number.isFinite(Date.parse(end)) ? end : null, location: str(e.location), description: str(e.description) },
      };
    }
    case 'LOCATION': {
      const l = raw.location;
      if (!isObject(l)) return null;
      return { type, caption: '', location: { latitude: num(l.latitude), longitude: num(l.longitude), label: str(l.label), address: str(l.address) } };
    }
    case 'CONTACT': {
      const c = raw.contact;
      if (!isObject(c) || !str(c.name)) return null;
      return { type, caption: '', contact: { name: str(c.name), phones: strings(c.phones), emails: strings(c.emails) } };
    }
    case 'STICKER':
      return stickerEmoji(str(raw.stickerId)) ? { type, caption: '', stickerId: str(raw.stickerId) } : null;
  }
  return null;
}

/** `metadata.groupPollVotes`: one per member (a later entry replaces an earlier one), no empty votes. */
export function parsePollVotes(metadata: unknown): GroupPollVote[] {
  if (!isObject(metadata) || !Array.isArray(metadata.groupPollVotes)) return [];
  const byMember = new Map<string, GroupPollVote>();
  for (const v of metadata.groupPollVotes) {
    if (!isObject(v) || !str(v.metaSiteId)) continue;
    const id = str(v.metaSiteId);
    byMember.delete(id);
    const optionIds = [...new Set(strings(v.optionIds))];
    if (optionIds.length) byMember.set(id, { metaSiteId: id, optionIds, date: str(v.date) || null });
  }
  return [...byMember.values()];
}

const RSVP_BY_NUMBER: Record<number, GroupRsvp> = { 1: 'GOING', 2: 'MAYBE', 3: 'NOT_GOING' };
/** `metadata.groupEventRsvps`: one per member, later replaces earlier; NO_RESPONSE (or 0) withdraws. */
export function parseEventRsvps(metadata: unknown): GroupEventRsvp[] {
  if (!isObject(metadata) || !Array.isArray(metadata.groupEventRsvps)) return [];
  const byMember = new Map<string, GroupEventRsvp>();
  for (const r of metadata.groupEventRsvps) {
    if (!isObject(r) || !str(r.metaSiteId)) continue;
    const id = str(r.metaSiteId);
    byMember.delete(id);
    const response = typeof r.response === 'number' ? RSVP_BY_NUMBER[r.response] : (['GOING', 'MAYBE', 'NOT_GOING'] as const).find((x) => x === r.response);
    if (response) byMember.set(id, { metaSiteId: id, response, date: str(r.date) || null });
  }
  return [...byMember.values()];
}

// ─── What a bubble draws ─────────────────────────────────────────────────────────────────────────────────────

export type GroupBubbleKind = 'image' | 'gif' | 'video' | 'document' | 'voice' | 'poll' | 'event' | 'location' | 'contact' | 'sticker';
const BUBBLE: Record<GroupContentType, GroupBubbleKind> = {
  IMAGE: 'image',
  GIF: 'gif',
  VIDEO: 'video',
  FILE: 'document',
  VOICE_NOTE: 'voice',
  POLL: 'poll',
  EVENT: 'event',
  LOCATION: 'location',
  CONTACT: 'contact',
  STICKER: 'sticker',
};
export const bubbleKind = (content: GroupContent): GroupBubbleKind => BUBBLE[content.type];
/** A sticker draws with no bubble behind it. */
export const drawsBubble = (content: GroupContent | null | undefined) => content?.type !== 'STICKER';
/** The words a menu Copy takes and an Edit starts from: the caption of a captioned type; nothing for the others. */
export const contentText = (content: GroupContent) => (takesCaption(content.type) ? content.caption : '');

/** A thumbnail's box from the sender's pixel size: aspect clamped to 0.5..2, at most `maxWidth` wide. */
export function thumbnailBox(media: Pick<GroupMedia, 'width' | 'height'>, maxWidth = 260): { width: number; height: number } {
  const aspect = media.width > 0 && media.height > 0 ? Math.min(2, Math.max(0.5, media.width / media.height)) : 4 / 3;
  return { width: maxWidth, height: Math.round(maxWidth / aspect) };
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MiB) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / MiB).toFixed(bytes < 10 * MiB ? 1 : 0)} MB`;
}

export function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toUpperCase().slice(0, 5) : '';
}

/** The voice player's speed pill: 1× → 1.5× → 2× → 1×. */
export const nextVoiceSpeed = (speed: number): number => (speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1);

// ─── Picking a file ──────────────────────────────────────────────────────────────────────────────────────────

/** The photo/video picker's kind for a picked file (GIF for image/gif); the document picker is always FILE. */
export function mediaTypeForPick(mime: string, from: 'media' | 'document'): GroupMediaType {
  if (from === 'document') return 'FILE';
  const m = normalizedMime(mime);
  if (m === 'image/gif') return 'GIF';
  if (m.startsWith('video/')) return 'VIDEO';
  return 'IMAGE';
}

export function mimeAllowed(type: GroupMediaType, mime: string): boolean {
  const m = normalizedMime(mime);
  switch (type) {
    case 'IMAGE':
      return IMAGE_MIMES.includes(m);
    case 'VIDEO':
      return VIDEO_MIMES.includes(m);
    case 'GIF':
      return GIF_MIMES.includes(m);
    case 'VOICE_NOTE':
      return m.startsWith('audio/') || m === 'video/webm' || m === 'video/mp4';
    case 'FILE':
      return true;
  }
}

export type MediaCheck = { ok: true; mimeType: string } | { ok: false; key: string; vars?: Record<string, string> };
const KIND_KEY: Record<GroupMediaType, string> = {
  IMAGE: 'chat.group.media.photo',
  VIDEO: 'chat.group.media.video',
  GIF: 'chat.group.media.gif',
  FILE: 'chat.group.media.document',
  VOICE_NOTE: 'chat.group.attach.voice',
};
/** A file about to be sent, checked BEFORE any call; `kindKey` is the localized {kind} of the type error. */
export function checkMedia(type: GroupMediaType, mime: string, sizeBytes: number): MediaCheck & { kindKey?: string } {
  const m = normalizedMime(mime) || (type === 'FILE' ? 'application/octet-stream' : '');
  if (sizeBytes <= 0) return { ok: false, key: 'chat.group.upload.empty' };
  if (!mimeAllowed(type, m)) return { ok: false, key: 'chat.group.upload.typeNotAllowed', kindKey: KIND_KEY[type] };
  if (sizeBytes > GROUP_MEDIA_MAX_BYTES[type]) return { ok: false, key: 'chat.group.upload.tooLarge', vars: { limit: GROUP_MEDIA_LIMIT_LABEL[type] } };
  return { ok: true, mimeType: m };
}

// ─── Creating a poll, an event, a place, a contact ───────────────────────────────────────────────────────────

export type Built<T> = { ok: true; value: T } | { ok: false; key: string };

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();

export function buildPoll(question: string, rawOptions: readonly string[], multipleAnswers: boolean): Built<GroupPoll> {
  const q = oneLine(question);
  if (!q || q.length > GROUP_CONTENT_RULES.maxQuestion) return { ok: false, key: 'chat.group.poll.needQuestion' };
  const texts = rawOptions.map(oneLine).filter(Boolean);
  if (texts.length < GROUP_CONTENT_RULES.minOptions || texts.some((t) => t.length > GROUP_CONTENT_RULES.maxOptionText)) return { ok: false, key: 'chat.group.poll.needOptions' };
  if (texts.length > GROUP_CONTENT_RULES.maxOptions) return { ok: false, key: 'chat.group.poll.needOptions' };
  if (new Set(texts.map((t) => t.toLowerCase())).size !== texts.length) return { ok: false, key: 'chat.group.poll.duplicateOption' };
  return { ok: true, value: { question: q, options: texts.map((text, i) => ({ id: String(i + 1), text })), multipleAnswers } };
}

/** The create form's rows: the filled ones, then exactly one empty row while fewer than 12 (never fewer than 2 rows). */
export function pollFormRows(rows: readonly string[]): string[] {
  const out = [...rows];
  while (out.length > 2 && !out[out.length - 1].trim() && !out[out.length - 2].trim()) out.pop();
  if (out.length < GROUP_CONTENT_RULES.maxOptions && out[out.length - 1]?.trim()) out.push('');
  while (out.length < 2) out.push('');
  return out;
}

/** The form's default start: the next full hour. */
export function defaultEventStart(now = new Date()): Date {
  const d = new Date(now);
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d;
}

export function buildEvent(input: { title: string; start: Date | null; end: Date | null; location: string; description: string }): Built<GroupCalendarEvent> {
  const title = oneLine(input.title);
  if (!title || title.length > GROUP_CONTENT_RULES.maxEventTitle) return { ok: false, key: 'chat.group.eventCard.needTitle' };
  if (!input.start || !Number.isFinite(input.start.getTime())) return { ok: false, key: 'chat.group.eventCard.needTitle' };
  if (input.end && input.end.getTime() < input.start.getTime()) return { ok: false, key: 'chat.group.eventCard.endBeforeStart' };
  return {
    ok: true,
    value: {
      title,
      startDate: input.start.toISOString(),
      endDate: input.end ? input.end.toISOString() : null,
      location: oneLine(input.location).slice(0, GROUP_CONTENT_RULES.maxEventLocation),
      description: input.description.trim().slice(0, GROUP_CONTENT_RULES.maxEventDescription),
    },
  };
}

/** "32.08, 34.78" or a Google / Apple / OpenStreetMap link → the point, or null. */
export function parseCoordinates(text: string): { latitude: number; longitude: number } | null {
  const s = text.trim();
  const patterns = [
    /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/, // google.com/maps/@lat,lng,z
    /[?&](?:q|ll|query|center|sll)=(-?\d+(?:\.\d+)?)(?:,|%2C)\s*(-?\d+(?:\.\d+)?)/i,
    /[#&]map=\d+\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)/, // openstreetmap.org/#map=z/lat/lng
    /[?&]mlat=(-?\d+(?:\.\d+)?)&mlon=(-?\d+(?:\.\d+)?)/,
    /^(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)$/,
  ];
  for (const re of patterns) {
    const m = s.match(re);
    if (!m) continue;
    const latitude = Number(m[1]);
    const longitude = Number(m[2]);
    if (Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180) return { latitude, longitude };
  }
  return null;
}

export function buildLocation(input: { latitude: number; longitude: number; label: string; address: string }): Built<GroupLocation> {
  const { latitude, longitude } = input;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
    return { ok: false, key: 'chat.group.location.invalidCoordinates' };
  }
  return {
    ok: true,
    value: {
      latitude,
      longitude,
      label: oneLine(input.label).slice(0, GROUP_CONTENT_RULES.maxLocationLabel),
      address: oneLine(input.address).slice(0, GROUP_CONTENT_RULES.maxLocationAddress),
    },
  };
}

const PHONE = /^\+?[0-9 ()\-.]{3,40}$/;
const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,189}\.[^\s@]{1,63}$/;
export const validPhone = (p: string) => PHONE.test(p.trim()) && (p.match(/\d/g)?.length ?? 0) >= 3;
export const validEmail = (e: string) => e.trim().length <= 254 && EMAIL.test(e.trim());

export function buildContact(input: { name: string; phones: readonly string[]; emails: readonly string[] }): Built<GroupContactCard> {
  const name = oneLine(input.name);
  if (!name || name.length > GROUP_CONTENT_RULES.maxContactName) return { ok: false, key: 'chat.group.contact.needName' };
  const phones = input.phones.map((p) => p.trim()).filter(Boolean);
  const emails = input.emails.map((e) => e.trim()).filter(Boolean);
  if (phones.some((p) => !validPhone(p))) return { ok: false, key: 'chat.group.contact.invalidPhone' };
  if (emails.some((e) => !validEmail(e))) return { ok: false, key: 'chat.group.contact.invalidEmail' };
  if (phones.length + emails.length === 0) return { ok: false, key: 'chat.group.contact.needValue' };
  return { ok: true, value: { name, phones: phones.slice(0, GROUP_CONTENT_RULES.maxContactValues), emails: emails.slice(0, GROUP_CONTENT_RULES.maxContactValues) } };
}

/** The `contentItem` a post carries (the wire shape `runGroupChatAction` takes). */
export function contentItemBody(content: Omit<GroupContent, 'caption'>): Record<string, unknown> {
  const { type } = content;
  if (isMediaType(type) && content.media) {
    const m = content.media;
    return {
      type,
      media: {
        mediaId: m.mediaId,
        mimeType: m.mimeType,
        sizeBytes: m.sizeBytes,
        ...(m.fileName ? { fileName: m.fileName } : {}),
        ...(m.width ? { width: m.width } : {}),
        ...(m.height ? { height: m.height } : {}),
        ...(m.durationMs ? { durationMs: m.durationMs } : {}),
        ...(type === 'VOICE_NOTE' && m.waveform.length ? { waveform: m.waveform } : {}),
      },
    };
  }
  if (type === 'POLL' && content.poll) return { type, poll: content.poll };
  if (type === 'EVENT' && content.event) {
    const e = content.event;
    return { type, event: { title: e.title, startDate: e.startDate, ...(e.endDate ? { endDate: e.endDate } : {}), ...(e.location ? { location: e.location } : {}), ...(e.description ? { description: e.description } : {}) } };
  }
  if (type === 'LOCATION' && content.location) {
    const l = content.location;
    return { type, location: { latitude: l.latitude, longitude: l.longitude, ...(l.label ? { label: l.label } : {}), ...(l.address ? { address: l.address } : {}) } };
  }
  if (type === 'CONTACT' && content.contact) return { type, contact: content.contact };
  return { type, stickerId: content.stickerId ?? '' };
}

/** The metadata a pending (not yet stored) typed post draws from, so its bubble is the real one. */
export function pendingContentMetadata(content: GroupContent): Record<string, unknown> {
  return { groupContent: { ...contentItemBody(content), ...(content.caption ? { caption: content.caption } : {}) } };
}

// ─── Polls ───────────────────────────────────────────────────────────────────────────────────────────────────

export function myPollChoice(votes: readonly GroupPollVote[], viewerId: string | undefined): string[] {
  return (viewerId && votes.find((v) => v.metaSiteId === viewerId)?.optionIds) || [];
}

/** A tap on an option: single answer — the same option withdraws, another replaces; multiple — it toggles. */
export function nextPollChoice(poll: GroupPoll, current: readonly string[], tapped: string): string[] {
  if (!poll.options.some((o) => o.id === tapped)) return [...current];
  if (!poll.multipleAnswers) return current.length === 1 && current[0] === tapped ? [] : [tapped];
  const set = new Set(current);
  if (set.has(tapped)) set.delete(tapped);
  else set.add(tapped);
  return poll.options.map((o) => o.id).filter((id) => set.has(id));
}

export function withMyVote(votes: readonly GroupPollVote[], viewerId: string, optionIds: string[], now = new Date()): GroupPollVote[] {
  const rest = votes.filter((v) => v.metaSiteId !== viewerId);
  return optionIds.length ? [...rest, { metaSiteId: viewerId, optionIds, date: now.toISOString() }] : rest;
}

export interface PollTally {
  voters: number;
  options: { id: string; text: string; count: number; fraction: number; mine: boolean; voterIds: string[] }[];
}
export function pollTally(poll: GroupPoll, votes: readonly GroupPollVote[], viewerId: string | undefined): PollTally {
  const valid = votes.map((v) => ({ ...v, optionIds: v.optionIds.filter((id) => poll.options.some((o) => o.id === id)) })).filter((v) => v.optionIds.length > 0);
  const voters = valid.length;
  const mine = new Set(myPollChoice(valid, viewerId));
  return {
    voters,
    options: poll.options.map((o) => {
      const voterIds = valid.filter((v) => v.optionIds.includes(o.id)).map((v) => v.metaSiteId);
      return { id: o.id, text: o.text, count: voterIds.length, fraction: voterIds.length / Math.max(voters, 1), mine: mine.has(o.id), voterIds };
    }),
  };
}

// ─── Events ──────────────────────────────────────────────────────────────────────────────────────────────────

export const myRsvp = (rsvps: readonly GroupEventRsvp[], viewerId: string | undefined): GroupRsvp | null =>
  (viewerId && rsvps.find((r) => r.metaSiteId === viewerId)?.response) || null;

/** A tap on an answer: the current one withdraws (NO_RESPONSE), another replaces it. */
export const nextRsvp = (current: GroupRsvp | null, tapped: GroupRsvp): GroupRsvp | 'NO_RESPONSE' => (current === tapped ? 'NO_RESPONSE' : tapped);

export function withMyRsvp(rsvps: readonly GroupEventRsvp[], viewerId: string, response: GroupRsvp | 'NO_RESPONSE', now = new Date()): GroupEventRsvp[] {
  const rest = rsvps.filter((r) => r.metaSiteId !== viewerId);
  return response === 'NO_RESPONSE' ? rest : [...rest, { metaSiteId: viewerId, response, date: now.toISOString() }];
}

export function rsvpCounts(rsvps: readonly GroupEventRsvp[]): Record<GroupRsvp, number> {
  const out: Record<GroupRsvp, number> = { GOING: 0, MAYBE: 0, NOT_GOING: 0 };
  for (const r of rsvps) out[r.response] += 1;
  return out;
}

const icsText = (s: string) => s.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/([,;])/g, '\\$1');
const icsDate = (iso: string) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
/** An .ics for "Add to calendar" on web (an hour long when it has no end). */
export function eventIcs(event: GroupCalendarEvent, uid: string): string {
  const end = event.endDate ?? new Date(Date.parse(event.startDate) + 60 * 60 * 1000).toISOString();
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Symphony//Group chat//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${icsDate(new Date().toISOString())}`,
    `DTSTART:${icsDate(event.startDate)}`,
    `DTEND:${icsDate(end)}`,
    `SUMMARY:${icsText(event.title)}`,
    ...(event.location ? [`LOCATION:${icsText(event.location)}`] : []),
    ...(event.description ? [`DESCRIPTION:${icsText(event.description)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}

// ─── Places and contacts ─────────────────────────────────────────────────────────────────────────────────────

export function mapsUrl(location: GroupLocation): string {
  return `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
}

export function contactVcf(contact: GroupContactCard): string {
  const esc = (s: string) => s.replace(/\\/g, '\\\\').replace(/([,;])/g, '\\$1');
  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${esc(contact.name)}`,
    ...contact.phones.map((p) => `TEL;TYPE=CELL:${p}`),
    ...contact.emails.map((e) => `EMAIL:${e}`),
    'END:VCARD',
    '',
  ].join('\r\n');
}

// ─── File links ──────────────────────────────────────────────────────────────────────────────────────────────

export interface MediaLink {
  url: string;
  expiresAt: number;
}
export type LinkState = { kind: 'fresh'; url: string } | { kind: 'stale' } | { kind: 'unavailable' } | { kind: 'unknown' };
/** A cached link is used while it has more than a minute left; an id the answer omitted waits 30 s before another ask. */
export function linkState(link: MediaLink | undefined, missedAt: number | undefined, now: number): LinkState {
  if (link && link.expiresAt - now > GROUP_CONTENT_RULES.linkFreshMs) return { kind: 'fresh', url: link.url };
  if (missedAt !== undefined && now - missedAt < GROUP_CONTENT_RULES.linkRetryMs) return { kind: 'unavailable' };
  return link ? { kind: 'stale' } : { kind: 'unknown' };
}
/** Which of the wanted ids to ask for now, in batches of at most 50. */
export function linkBatches(wanted: readonly string[], links: Record<string, MediaLink>, missed: Record<string, number>, now: number): string[][] {
  const ask = [...new Set(wanted)].filter((id) => {
    const s = linkState(links[id], missed[id], now);
    return s.kind === 'stale' || s.kind === 'unknown';
  });
  const out: string[][] = [];
  for (let i = 0; i < ask.length; i += GROUP_CONTENT_RULES.maxLinkBatch) out.push(ask.slice(i, i + GROUP_CONTENT_RULES.maxLinkBatch));
  return out;
}

// ─── Voice notes ─────────────────────────────────────────────────────────────────────────────────────────────

/** Level samples → 64 bars (bucket averages), the loudest = 100, each an int 0..100; silence is all zeros. */
export function waveformBars(samples: readonly number[], bars: number = GROUP_CONTENT_RULES.waveformBars): number[] {
  if (samples.length === 0) return [];
  const out: number[] = [];
  for (let i = 0; i < bars; i++) {
    const from = Math.floor((i * samples.length) / bars);
    const to = Math.max(from + 1, Math.floor(((i + 1) * samples.length) / bars));
    const bucket = samples.slice(from, Math.min(to, samples.length));
    out.push(bucket.length ? bucket.reduce((a, b) => a + Math.abs(b), 0) / bucket.length : 0);
  }
  const max = Math.max(...out);
  return out.map((v) => (max > 0 ? Math.max(0, Math.min(100, Math.round((v / max) * 100))) : 0));
}

/**
 * A recorded voice note → the VOICE_NOTE media send, or why not (too short, or a type / size the group refuses). THE ONE
 * decision both entry points share — the composer's mic (tap to record) and Attach → Voice message — so a group voice
 * note always goes through the group media path (media-upload → PUT → post-message), never the private send. iOS /
 * Android `GroupContent.voiceNoteSend` decide the same.
 */
export type VoiceNoteSend =
  | { ok: true; type: 'VOICE_NOTE'; fileName: string; mimeType: string; durationMs: number; waveform: number[] }
  | { ok: false; key: string; vars?: Record<string, string | number> };
export function voiceNoteSend(rec: { sizeBytes: number; mimeType: string; durationMs: number; samples: readonly number[] }, now: number = Date.now()): VoiceNoteSend {
  if (rec.durationMs < GROUP_CONTENT_RULES.voiceMinMs) return { ok: false, key: 'chat.group.voice.tooShort' };
  const check = checkMedia('VOICE_NOTE', rec.mimeType, rec.sizeBytes);
  if (!check.ok) return { ok: false, key: check.key, ...(check.vars ? { vars: check.vars } : {}) };
  const ext = normalizedMime(rec.mimeType) === 'audio/webm' ? 'webm' : 'm4a';
  return { ok: true, type: 'VOICE_NOTE', fileName: `voice-${now}.${ext}`, mimeType: check.mimeType, durationMs: Math.round(rec.durationMs), waveform: waveformBars(rec.samples) };
}

// ─── The upload of one typed message ─────────────────────────────────────────────────────────────────────────

export type UploadPhase = 'requesting' | 'uploading' | 'posting' | 'sent' | 'failed' | 'cancelled';
export interface UploadState {
  phase: UploadPhase;
  progress: number;
  mediaId: string | null;
  errorKey: string | null;
}
export type UploadEvent =
  | { kind: 'start' }
  | { kind: 'minted' }
  | { kind: 'progress'; fraction: number }
  | { kind: 'uploaded'; mediaId: string }
  | { kind: 'posted' }
  | { kind: 'failed'; errorKey: string }
  | { kind: 'cancel' }
  | { kind: 'retry' };

export const UPLOAD_START: UploadState = { phase: 'requesting', progress: 0, mediaId: null, errorKey: null };

/** The one transition table every surface runs (cancel only before the post; retry skips the upload once a file id exists). */
export function nextUpload(state: UploadState, event: UploadEvent): UploadState {
  switch (event.kind) {
    case 'start':
      return UPLOAD_START;
    case 'minted':
      return state.phase === 'requesting' ? { ...state, phase: 'uploading', progress: 0 } : state;
    case 'progress':
      return state.phase === 'uploading' ? { ...state, progress: Math.max(state.progress, Math.min(1, Math.max(0, event.fraction))) } : state;
    case 'uploaded':
      return state.phase === 'uploading' ? { ...state, phase: 'posting', progress: 1, mediaId: event.mediaId } : state;
    case 'posted':
      return state.phase === 'posting' ? { ...state, phase: 'sent' } : state;
    case 'failed':
      return state.phase === 'sent' || state.phase === 'cancelled' ? state : { ...state, phase: 'failed', errorKey: event.errorKey };
    case 'cancel':
      return state.phase === 'requesting' || state.phase === 'uploading' ? { ...state, phase: 'cancelled' } : state;
    case 'retry':
      if (state.phase !== 'failed') return state;
      return state.mediaId ? { ...state, phase: 'posting', errorKey: null } : { ...UPLOAD_START };
  }
}

export const canCancelUpload = (s: UploadState) => s.phase === 'requesting' || s.phase === 'uploading';

/** A refusal (or a failure) of an upload or its post → the copy the pending bubble shows. */
export function uploadErrorKey(refusal: string | null, refusalKey: (r: string | null) => string): string {
  if (refusal === 'MEDIA_NOT_ALLOWED') return 'chat.group.error.mediaNotAllowed';
  if (refusal === 'MEDIA_NOT_FOUND') return 'chat.group.error.mediaNotFound';
  // A transport failure (no refusal) stays "Upload failed".
  if (!refusal) return 'chat.group.upload.failed';
  // A refusal with copy of its own keeps it (NOT_A_MEMBER, GROUP_ENDED, SEND_RESTRICTED…); one that would read the
  // generic "Something went wrong" says the media was not allowed instead (2026-10-31 rerun, D6).
  const key = refusalKey(refusal);
  return key === 'chat.group.error.generic' ? 'chat.group.error.mediaNotAllowed' : key;
}

/** Wix Media's answer to the PUT: `file.id` (or a bare `id`), or null. */
export function uploadedMediaId(json: unknown): string | null {
  if (!isObject(json)) return null;
  const file = isObject(json.file) ? json.file : null;
  const id = str(file?.id) || str(json.id);
  return id || null;
}
